import type { Insights, QueryResult, ResultValue } from './api';

/**
 * Exportable report (§ AI Prompt Reporting — the "& Reports" half of deliverable #7).
 * Bundles a question's answer into ONE self-contained, printable HTML document:
 * executive summary, KPIs, chart, trend, root cause, data table, SQL, and
 * recommendations. No external assets — opens in any browser and prints to PDF.
 */
export function downloadReport(question: string, result: QueryResult, insights: Insights | null): void {
  const html = buildReportHtml(question, result, insights);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `report-${slug(question)}-${dateStamp()}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** One report within a dashboard export. */
export interface DashboardItem {
  title: string;
  result: QueryResult;
  insights: Insights | null;
}

/** Download a whole dashboard (a report template) as one self-contained HTML document. */
export function downloadDashboardReport(name: string, items: DashboardItem[]): void {
  const html = buildDashboardReportHtml(name, items);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `dashboard-${slug(name)}-${dateStamp()}.html`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function buildDashboardReportHtml(name: string, items: DashboardItem[]): string {
  const generated = new Date().toLocaleString();
  const sections = items
    .map(
      (it) => `<section class="report">
      <h2 class="report-title">${esc(it.title)}</h2>
      ${it.insights?.executiveSummary ? `<p class="summary">${esc(it.insights.executiveSummary)}</p>` : ''}
      ${kpiSection(it.insights)}
      ${chartSection(it.result)}
      ${tableSection(it.result)}
    </section>`,
    )
    .join('');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Dashboard — ${esc(name)}</title>
<style>${STYLES}</style></head>
<body>
<main>
  <header>
    <div class="eyebrow">AI Prompt Reporting · Dashboard</div>
    <h1>${esc(name)}</h1>
    <div class="meta">Generated ${esc(generated)} · ${items.length} report${items.length === 1 ? '' : 's'}</div>
  </header>
  ${sections || '<p class="summary">This dashboard has no reports with data.</p>'}
  <footer>Generated by dgt-ai-reporting · print to PDF to save</footer>
</main>
</body></html>`;
}

export function buildReportHtml(question: string, result: QueryResult, insights: Insights | null): string {
  const generated = new Date().toLocaleString();
  const sourceBadge = insights
    ? `<span class="badge ${insights.source === 'llm' ? 'ai' : 'calc'}">${insights.source === 'llm' ? 'AI-generated' : 'computed'}</span>`
    : '';

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Report — ${esc(question)}</title>
<style>${STYLES}</style></head>
<body>
<main>
  <header>
    <div class="eyebrow">AI Prompt Reporting</div>
    <h1>${esc(question)}</h1>
    <div class="meta">Generated ${esc(generated)} ${sourceBadge}</div>
  </header>
  ${insights?.executiveSummary ? `<p class="summary">${esc(insights.executiveSummary)}</p>` : ''}
  ${kpiSection(insights)}
  ${chartSection(result)}
  ${trendSection(insights)}
  ${rootCauseSection(insights)}
  ${tableSection(result)}
  ${recommendationsSection(insights)}
  ${sqlSection(result)}
  <footer>Generated by dgt-ai-reporting · print to PDF to save</footer>
</main>
</body></html>`;
}

function kpiSection(insights: Insights | null): string {
  if (!insights?.kpis.length) return '';
  const cards = insights.kpis
    .map(
      (k) => `<div class="kpi"><div class="kpi-label">${esc(k.label)}</div>
      <div class="kpi-value">${esc(k.value)}${k.delta ? ` <span class="delta">${esc(k.delta)}</span>` : ''}</div>
      ${k.note ? `<div class="kpi-note">${esc(k.note)}</div>` : ''}</div>`,
    )
    .join('');
  return `<section><h2>Key metrics</h2><div class="kpis">${cards}</div></section>`;
}

/**
 * The chart for a result, honouring the plan's `visualization`.
 *
 * The export is a single self-contained file with no scripts or external assets,
 * so it can be opened offline or printed — ECharts (which draws the on-screen
 * chart) is not available here. Bars are styled divs; a pie needs real geometry,
 * so it is emitted as inline SVG. Anything else falls back to bars, which read
 * acceptably for a line or a plain ranking.
 */
function chartSection(result: QueryResult): string {
  const metric = result.columns.find((c) => c.kind === 'metric');
  const label = result.columns.find((c) => c.kind === 'dimension' || c.kind === 'time');
  if (!metric || !label || result.rows.length < 2) return '';
  const points = result.rows.map((r) => ({ label: String(r[label.key] ?? '—'), value: Number(r[metric.key]) || 0 }));
  const heading = `${esc(metric.label)} by ${esc(label.label)}`;
  const viz = result.plan?.visualization;
  const body =
    viz === 'pie' || viz === 'donut' ? pieSvg(points, viz === 'donut') : barRows(points);
  return `<section><h2>${heading}</h2>${body}</section>`;
}

/** Horizontal bars — the default, and the fallback for every non-pie chart. */
function barRows(points: { label: string; value: number }[]): string {
  const max = Math.max(...points.map((p) => Math.abs(p.value)), 1);
  const bars = points
    .map(
      (p) => `<div class="bar-row"><span class="bar-label" title="${esc(p.label)}">${esc(p.label)}</span>
      <span class="bar-track"><span class="bar-fill" style="width:${Math.max(1, (Math.abs(p.value) / max) * 100)}%"></span></span>
      <span class="bar-value">${esc(fmt(p.value))}</span></div>`,
    )
    .join('');
  return `<div class="bars">${bars}</div>`;
}

/** Slice colours — the categorical ramp the on-screen chart uses, so an exported
 *  report is recognisably the same chart. */
const PIE_COLORS = ['#2f6fed', '#22a06b', '#e8833a', '#8b5cf6', '#e5484d', '#f5c518', '#0ea5e9', '#ec4899'];

/**
 * A pie/donut as inline SVG: one wedge per slice, plus a legend carrying the
 * value and its share — the same `LABEL: 9 (6%)` reading as the on-screen chart.
 *
 * Negative values have no meaningful share of a whole, so they are treated as
 * their magnitude; a total of zero would make every angle NaN, so that degrades
 * to bars rather than drawing an empty circle.
 */
function pieSvg(points: { label: string; value: number }[], donut: boolean): string {
  const slices = points.map((p) => ({ ...p, value: Math.abs(p.value) }));
  const total = slices.reduce((sum, p) => sum + p.value, 0);
  if (total <= 0) return barRows(points);

  const R = 90;
  const C = 100; // centre of a 200×200 viewBox
  let angle = -Math.PI / 2; // start at 12 o'clock, like the on-screen chart
  const wedges = slices
    .map((p, i) => {
      const sweep = (p.value / total) * Math.PI * 2;
      const color = PIE_COLORS[i % PIE_COLORS.length];
      // A single slice is the whole circle; an arc from a point back to itself
      // draws nothing, so emit a circle instead.
      if (slices.length === 1 || sweep >= Math.PI * 2 - 1e-9) {
        return `<circle cx="${C}" cy="${C}" r="${R}" fill="${color}" />`;
      }
      const [x1, y1] = [C + R * Math.cos(angle), C + R * Math.sin(angle)];
      angle += sweep;
      const [x2, y2] = [C + R * Math.cos(angle), C + R * Math.sin(angle)];
      const large = sweep > Math.PI ? 1 : 0;
      return `<path d="M ${C} ${C} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z" fill="${color}" />`;
    })
    .join('');
  // The hole is painted, not cut: printers and offline viewers handle a plain
  // white circle far more reliably than a mask or even-odd fill rule.
  const hole = donut ? `<circle cx="${C}" cy="${C}" r="${R * 0.55}" fill="#fff" />` : '';

  const legend = slices
    .map((p, i) => {
      const pct = Math.round((p.value / total) * 100);
      return `<div class="pie-key"><span class="pie-swatch" style="background:${PIE_COLORS[i % PIE_COLORS.length]}"></span>
      <span class="pie-name" title="${esc(p.label)}">${esc(p.label)}</span>
      <span class="pie-val">${esc(fmt(p.value))} (${pct}%)</span></div>`;
    })
    .join('');

  return `<div class="pie-wrap">
    <svg viewBox="0 0 200 200" width="200" height="200" role="img" aria-label="Pie chart">${wedges}${hole}</svg>
    <div class="pie-legend">${legend}</div>
  </div>`;
}

function trendSection(insights: Insights | null): string {
  if (!insights?.trend) return '';
  const t = insights.trend;
  const arrow = t.direction === 'up' ? '▲' : t.direction === 'down' ? '▼' : '▶';
  const cls = t.direction === 'up' ? 'up' : t.direction === 'down' ? 'down' : 'flat';
  const pct = t.changePct != null ? ` ${t.changePct > 0 ? '+' : ''}${t.changePct}%` : '';
  return `<section><h2>Trend</h2><p><span class="trend ${cls}">${arrow}${esc(pct)}</span> ${esc(t.narrative)}</p></section>`;
}

function rootCauseSection(insights: Insights | null): string {
  if (!insights?.rootCause) return '';
  const rc = insights.rootCause;
  const rows = rc.drivers
    .map(
      (d) => `<div class="bar-row"><span class="bar-label" title="${esc(d.label)}">${esc(d.label)}</span>
      <span class="bar-track"><span class="bar-fill accent" style="width:${Math.max(1, Math.min(100, d.share))}%"></span></span>
      <span class="bar-value">${d.share}% · ${esc(d.value)}</span></div>`,
    )
    .join('');
  return `<section><h2>Root cause</h2><p>${esc(rc.narrative)}</p><div class="bars">${rows}</div></section>`;
}

function recommendationsSection(insights: Insights | null): string {
  if (!insights?.recommendations.length) return '';
  const items = insights.recommendations.map((r) => `<li>${esc(r)}</li>`).join('');
  return `<section><h2>Recommended actions</h2><ul>${items}</ul></section>`;
}

function tableSection(result: QueryResult): string {
  if (result.rows.length === 0) return '';
  const head = result.columns.map((c) => `<th>${esc(c.label)}</th>`).join('');
  const body = result.rows
    .map((row) => `<tr>${result.columns.map((c) => `<td>${esc(fmtVal(row[c.key]))}</td>`).join('')}</tr>`)
    .join('');
  return `<section><h2>Data</h2><div class="table-wrap"><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div></section>`;
}

function sqlSection(result: QueryResult): string {
  if (!result.sql) return '';
  return `<section><h2>Generated SQL</h2><pre>${esc(result.sql)}</pre></section>`;
}

// ── helpers ──
function esc(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
function fmt(n: number): string {
  return Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : String(n);
}
function fmtVal(v: ResultValue): string {
  return typeof v === 'number' ? fmt(v) : v == null ? '—' : String(v);
}
function slug(s: string): string {
  return (s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)) || 'report';
}
function dateStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

const STYLES = `
:root { color-scheme: light; }
* { box-sizing: border-box; }
body { margin: 0; background: #f6f7f9; color: #1c2024; font: 15px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
main { max-width: 820px; margin: 0 auto; padding: 40px 28px 64px; background: #fff; }
header { border-bottom: 2px solid #eceef1; padding-bottom: 18px; margin-bottom: 24px; }
.eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; }
h1 { font-size: 26px; margin: 6px 0 8px; line-height: 1.25; }
.meta { font-size: 13px; color: #6b7280; display: flex; align-items: center; gap: 8px; }
.badge { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; }
.badge.ai { color: #4338ca; background: #eef2ff; }
.badge.calc { color: #475569; background: #f1f5f9; }
.summary { font-size: 16px; color: #374151; background: #f8fafc; border-left: 3px solid #4f46e5; padding: 12px 16px; border-radius: 0 8px 8px 0; margin: 0 0 24px; }
h2 { font-size: 15px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; margin: 28px 0 12px; }
section { page-break-inside: avoid; }
.kpis { display: flex; flex-wrap: wrap; gap: 12px; }
.kpi { flex: 1 1 150px; min-width: 130px; padding: 12px 14px; border: 1px solid #eceef1; border-radius: 10px; background: #fbfbfc; }
.kpi-label { font-size: 12px; color: #6b7280; margin-bottom: 4px; }
.kpi-value { font-size: 24px; font-weight: 700; font-variant-numeric: tabular-nums; }
.delta { font-size: 13px; font-weight: 600; }
.kpi-note { font-size: 11.5px; color: #9ca3af; margin-top: 2px; }
.bars { display: flex; flex-direction: column; gap: 7px; }
.pie-wrap { display: flex; align-items: center; gap: 24px; flex-wrap: wrap; }
.pie-legend { display: flex; flex-direction: column; gap: 6px; min-width: 240px; }
.pie-key { display: flex; align-items: center; gap: 8px; font-size: 13px; }
.pie-swatch { width: 10px; height: 10px; border-radius: 2px; flex: 0 0 auto; }
.pie-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pie-val { font-variant-numeric: tabular-nums; color: #374151; }
.bar-row { display: flex; align-items: center; gap: 10px; font-size: 13px; }
.bar-label { flex: 0 0 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bar-track { flex: 1; height: 12px; background: #eef0f3; border-radius: 999px; overflow: hidden; }
.bar-fill { display: block; height: 100%; background: #4f46e5; border-radius: 999px; }
.bar-fill.accent { background: #4f46e5; }
.bar-value { flex: 0 0 130px; text-align: right; font-variant-numeric: tabular-nums; color: #374151; }
.trend { font-weight: 700; }
.trend.up { color: #16a34a; } .trend.down { color: #dc2626; } .trend.flat { color: #6b7280; }
ul { margin: 0; padding-left: 20px; } li { margin: 4px 0; }
.table-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #eceef1; }
th { color: #6b7280; font-weight: 600; }
td { font-variant-numeric: tabular-nums; }
pre { background: #0f172a; color: #e2e8f0; padding: 14px 16px; border-radius: 10px; overflow-x: auto; font-size: 12.5px; line-height: 1.5; }
footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #eceef1; font-size: 12px; color: #9ca3af; }
@media print { body { background: #fff; } main { max-width: none; padding: 0; } .summary { break-inside: avoid; } }
`;
